import { useState } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { submitInquiry } from "@/lib/api";
import { track } from "@/lib/tracker";
import { useSiteContent } from "@/lib/siteContent";

const buildFaqs = (sc) => [
  { q: "Is this a multi-week class?", a: `No. This is a live ${sc.lab_duration_label} online build lab. One session. Hands-on. Then you walk away with momentum.` },
  { q: "Do I need to know how to code?", a: "No. This is designed for beginners and non-technical leaders. If you can describe what you want, you can build it." },
  { q: `Will I master AI building in ${sc.lab_duration_label}?`, a: "No. The goal is to help you understand the process, see what's possible, and begin building with confidence — not to make you an expert overnight." },
  { q: "What will I build?", a: "You may begin building or mapping a website, landing page, app idea, dashboard, intake system, or funnel — depending on your goal." },
  { q: "Can I use this skill to make money?", a: "This course does not guarantee income, but it teaches a practical digital skill that can be developed and potentially offered as a service to others with continued practice." },
  { q: "How much is it?", a: `The live build lab is ${sc.lab_price_label} — one-time, all-in. Klarna & Afterpay are available at checkout if you'd like to split the payment.` },
  { q: "When is the next session?", a: `${sc.lab_date_primary_label} · ${sc.lab_date_primary_time}. The session after that: ${sc.lab_date_secondary_label} — ${sc.lab_date_secondary_status}.` },
  { q: "Is this only for business owners?", a: "No. It's also for nonprofit leaders, professionals, consultants, coaches, creatives, and anyone who wants to learn how to build smarter with AI." },
];

export default function FAQ() {
  const sc = useSiteContent();
  const FAQS = buildFaqs(sc);
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const valid = form.name.trim().length > 1 && /\S+@\S+\.\S+/.test(form.email) && form.message.trim().length >= 4;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) {
      toast.error("Please share your name, a valid email, and a short message.");
      return;
    }
    setSubmitting(true);
    try {
      await submitInquiry({ name: form.name.trim(), email: form.email.trim(), message: form.message.trim() });
      track("inquiry_submit", { src: "faq" });
      setDone(true);
      toast.success("Got it. We'll get back to you within 24 hours.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="faq" data-testid="faq-section" className="relative py-24 lg:py-32 border-t border-[var(--line)]">
      <div className="max-w-4xl mx-auto px-6 lg:px-10">
        <div className="eyebrow mb-5">Frequently Asked</div>
        <h2 className="font-display text-[44px] sm:text-5xl lg:text-[72px] leading-[1.02] tracking-[-0.035em] text-ink mb-12">
          Questions, <span className="font-serif-em italic text-brand font-light">answered.</span>
        </h2>

        <Accordion type="single" collapsible className="border-t border-[var(--line)]">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-item-${i}`} className="border-b border-[var(--line)] py-2">
              <AccordionTrigger className="text-left text-ink text-lg sm:text-xl font-display tracking-[-0.015em] hover:no-underline py-5">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-ink/75 leading-relaxed pb-6 text-[16px] max-w-3xl">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        <div id="contact" data-testid="faq-contact" className="mt-16 rounded-2xl border border-[var(--line)] bg-bone p-6 sm:p-10">
          <div className="grid lg:grid-cols-[1fr_1.4fr] gap-8 lg:gap-12 items-start">
            <div>
              <div className="eyebrow mb-3">Still have questions?</div>
              <h3 className="font-display text-3xl sm:text-4xl tracking-[-0.025em] text-ink leading-[1.05] mb-4">
                Send us a <span className="font-serif-em italic text-brand font-light">message.</span>
              </h3>
              <p className="text-ink/75 text-[15px] leading-relaxed">
                Tell us what&apos;s on your mind &mdash; about the lab, scheduling, or anything else. We read every message and reply within 24 hours.
              </p>
            </div>

            {done ? (
              <div data-testid="inquiry-success" className="flex flex-col items-start gap-4 p-6 rounded-2xl bg-white border border-[var(--line)]">
                <CheckCircle2 className="text-brand" size={28} />
                <div>
                  <div className="font-display text-2xl tracking-[-0.02em] text-ink mb-1">Message sent.</div>
                  <p className="text-ink/70 text-[15px] leading-relaxed">
                    Thanks, <strong>{form.name.trim() || "friend"}</strong>. We&apos;ll be in touch within 24 hours at <strong>{form.email}</strong>.
                  </p>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="inquiry-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="iq_name" className="text-[11px] uppercase tracking-[0.18em] text-ink/60 font-medium font-mono">Your name *</Label>
                    <Input
                      id="iq_name"
                      data-testid="inquiry-name"
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                      className="bg-white border border-[var(--line)] text-ink h-11 mt-1.5 focus-visible:ring-brand focus-visible:ring-offset-0"
                    />
                  </div>
                  <div>
                    <Label htmlFor="iq_email" className="text-[11px] uppercase tracking-[0.18em] text-ink/60 font-medium font-mono">Email *</Label>
                    <Input
                      id="iq_email"
                      type="email"
                      data-testid="inquiry-email"
                      value={form.email}
                      onChange={(e) => update("email", e.target.value)}
                      className="bg-white border border-[var(--line)] text-ink h-11 mt-1.5 focus-visible:ring-brand focus-visible:ring-offset-0"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="iq_message" className="text-[11px] uppercase tracking-[0.18em] text-ink/60 font-medium font-mono">Your question *</Label>
                  <Textarea
                    id="iq_message"
                    data-testid="inquiry-message"
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    rows={5}
                    placeholder="What's on your mind?"
                    className="bg-white border border-[var(--line)] text-ink mt-1.5 focus-visible:ring-brand focus-visible:ring-offset-0"
                  />
                </div>
                <Button
                  type="submit"
                  data-testid="inquiry-submit"
                  disabled={submitting || !valid}
                  className="btn-ink rounded-full h-11 px-6"
                >
                  {submitting ? <Loader2 className="animate-spin" size={14} /> : <><Send size={13} className="mr-2" /> Send message</>}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
